import { createTheme } from "@mui/material/styles";
import { FONT_BODY, FONT_DISPLAY } from "./designTokens";

// Modern DoorDash/UberEats inspired theme
const theme = createTheme({
  breakpoints: {
    keys: ["xs", "sm", "md", "lg", "xl"],
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1400 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 24px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 24px rgba(0,0,0,0.14)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
          },
        },
      },
    },
    // Every popup and sheet: the page behind dims and blurs a little, the way
    // components/Dialog/RmfDialog.js draws it. Menus, selects and popovers use
    // an invisible backdrop and stay as they are; a popup that sets its own
    // backdrop or paper (sx / PaperProps) keeps it.
    MuiBackdrop: {
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.invisible ? {} : { backgroundColor: "rgba(26,25,24,0.55)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)" },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ ownerState }) =>
          ownerState.fullScreen ? {} : { borderRadius: 24, boxShadow: "0 24px 60px rgba(0,0,0,0.30)" },
      },
    },
  },
  typography: {
    // Inter for body and UI; headings (h1–h6) use the display face.
    fontFamily: FONT_BODY,
    htmlFontSize: 16,
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontWeightBolder: 800,
    h1: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      fontSize: "3.5rem",
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      fontSize: "2.5rem",
      lineHeight: 1.25,
      letterSpacing: "-0.02em",
    },
    h3: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.3,
      letterSpacing: "-0.02em",
    },
    h4: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: "1.5rem",
      lineHeight: 1.35,
      letterSpacing: "-0.015em",
    },
    h5: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: "1.25rem",
      lineHeight: 1.4,
      letterSpacing: "-0.01em",
    },
    h6: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      fontSize: "1.125rem",
      lineHeight: 1.5,
      letterSpacing: "-0.01em",
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: 1.5,
      letterSpacing: "0em",
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.5,
      letterSpacing: "0em",
    },
    body1: {
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: 1.6,
      letterSpacing: "0em",
    },
    body2: {
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: 1.6,
      letterSpacing: "0em",
    },
    button: {
      fontWeight: 600,
      fontSize: "0.875rem",
      lineHeight: 1.5,
      letterSpacing: "0.01em",
      textTransform: "none",
    },
    caption: {
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 1.5,
      letterSpacing: "0.02em",
    },
    overline: {
      fontWeight: 600,
      fontSize: "0.75rem",
      lineHeight: 2,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    },
  },
  shape: {
    borderRadius: 12,
  },
  palette: {
    mode: "light",
    common: {
      black: "#191919",
      blackShade: "rgba(25, 25, 25, 0.55)",
      lightBlack: "rgba(25, 25, 25, 0.25)",
      darkBlack: "rgba(25, 25, 25, 0.37)",
      white: "#FFFFFF",
      whiteShade: "#F7F7F7",
    },
    primary: {
      main: "#FF3008", // DoorDash red / UberEats inspired
      light: "#FF6B4A",
      lightest: "#FFEBE6",
      dark: "#CC2606",
      darkest: "#991D05",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#1F1F1F",
      light: "#4A4A4A",
      lightest: "#767676",
      dark: "#0D0D0D",
      darkest: "#000000",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#00A651",
      light: "#4CD964",
      lightest: "#E8F8EE",
      dark: "#008040",
      darkest: "#006030",
      contrastText: "#FFFFFF",
    },
    info: {
      main: "#0066FF",
      light: "#4D94FF",
      lightest: "#E6F0FF",
      dark: "#0052CC",
      darkest: "#003D99",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#FF3B30",
      light: "#FF6961",
      lightest: "#FFEBE9",
      dark: "#CC2F26",
      darkest: "#99231C",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#FF9500",
      light: "#FFB84D",
      lightest: "#FFF5E6",
      dark: "#CC7700",
      darkest: "#995A00",
      contrastText: "#191919",
    },
    button: {
      main: "#FF3008",
      light: "#FF6B4A",
      lightest: "#FFEBE6",
      dark: "#CC2606",
      darkest: "#991D05",
      contrastText: "#FFFFFF",
    },
    grey: {
      50: "#FAFAFA",
      100: "#F5F5F5",
      200: "#EEEEEE",
      300: "#E0E0E0",
      400: "#BDBDBD",
      500: "#9E9E9E",
      600: "#767676",
      700: "#616161",
      800: "#424242",
      900: "#1F1F1F",
      A100: "#F5F5F5",
      A200: "#EEEEEE",
      A400: "#BDBDBD",
      A700: "#616161",
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    text: {
      primary: "#191919",
      secondary: "#767676",
      success: "#00A651",
      disabled: "#BDBDBD",
      hint: "#9E9E9E",
    },
    divider: "rgba(0, 0, 0, 0.08)",
    background: {
      primary: "#FFFFFF",
      secondary: "#FAFAFA",
      success: "#E8F8EE",
      paper: "#FFFFFF",
      default: "#FAFAFA",
    },
    // Custom colors for food delivery
    delivery: {
      fast: "#00A651",
      medium: "#FF9500",
      slow: "#FF3B30",
    },
    rating: {
      star: "#FFB800",
      text: "#191919",
    },
  },
  shadows: [
    "none",
    "0 1px 2px rgba(0,0,0,0.04)",
    "0 2px 4px rgba(0,0,0,0.06)",
    "0 2px 8px rgba(0,0,0,0.08)",
    "0 4px 12px rgba(0,0,0,0.1)",
    "0 6px 16px rgba(0,0,0,0.12)",
    "0 8px 24px rgba(0,0,0,0.14)",
    "0 12px 32px rgba(0,0,0,0.16)",
    "0 16px 40px rgba(0,0,0,0.18)",
    "0 20px 48px rgba(0,0,0,0.2)",
    "0 24px 56px rgba(0,0,0,0.22)",
    "0 28px 64px rgba(0,0,0,0.24)",
    "0 32px 72px rgba(0,0,0,0.26)",
    "0 36px 80px rgba(0,0,0,0.28)",
    "0 40px 88px rgba(0,0,0,0.3)",
    "0 44px 96px rgba(0,0,0,0.32)",
    "0 48px 104px rgba(0,0,0,0.34)",
    "0 52px 112px rgba(0,0,0,0.36)",
    "0 56px 120px rgba(0,0,0,0.38)",
    "0 60px 128px rgba(0,0,0,0.4)",
    "0 64px 136px rgba(0,0,0,0.42)",
    "0 68px 144px rgba(0,0,0,0.44)",
    "0 72px 152px rgba(0,0,0,0.46)",
    "0 76px 160px rgba(0,0,0,0.48)",
    "0 80px 168px rgba(0,0,0,0.5)",
  ],
});

export default theme;
